import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiCheckLine, RiFileCopyLine } from '@remixicon/react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '../ui'

interface SetupLinkDialogProps {
  /** Il link e per chi è. `null` chiude il dialogo. */
  link: { url: string; name: string } | null
  onClose: () => void
}

/** Quanto vale il link: lo stesso numero che il server usa per scaderne uno. */
const LINK_VALID_DAYS = 7

/**
 * Il link di attivazione di un account, mostrato una volta sola.
 *
 * Nel database c'è solo l'hash, quindi questo è l'unico momento in cui il link
 * esiste per intero. Il dialogo lo dice: chi lo chiude senza copiarlo deve
 * sapere che non lo rivedrà, e che ne può generare uno nuovo.
 */
export default function SetupLinkDialog({ link, onClose }: SetupLinkDialogProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    if (!link) return
    // Il campo resta selezionabile: se la copia è negata (contesto non sicuro,
    // permesso rifiutato) si copia a mano, e il link non si rivede.
    try {
      await navigator.clipboard.writeText(link.url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Dialog open={link !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.setupLinkTitle')}</DialogTitle>
          <DialogDescription>
            {t('admin.setupLinkDescription', { name: link?.name ?? '', days: LINK_VALID_DAYS })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            readOnly
            value={link?.url ?? ''}
            aria-label={t('admin.setupLinkTitle')}
            className="font-mono text-xs"
          />
          <Button type="button" variant="secondary" onClick={copy}>
            {copied ? (
              <RiCheckLine className="size-4 text-emerald-600" aria-hidden="true" />
            ) : (
              <RiFileCopyLine className="size-4" aria-hidden="true" />
            )}
            <span className="sr-only">{t('common.copy')}</span>
          </Button>
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('admin.setupLinkNotShownAgain')}
        </p>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
